import React, { useEffect, useState } from 'react';
import { useGameLounge } from '../context/GameLoungeContext';
import StationCard from './StationCard';
import KioskModal from './KioskModal';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { FaPlaystation } from 'react-icons/fa';

const { FiMonitor, FiDollarSign, FiPackage, FiUsers } = FiIcons;

function Dashboard() {
  const { state, dispatch } = useGameLounge();
  const [selectedStation, setSelectedStation] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(state.rooms[0]?.id || null);

  useEffect(() => {
    dispatch({ type: 'UPDATE_CURRENT_AMOUNTS' });
  }, [dispatch]);

  const getAllStations = () => {
    return state.rooms.flatMap(room => room.stations);
  };

  const allStations = getAllStations();
  const occupiedStations = allStations.filter(station => station.status === 'occupied');
  const occupiedPcs = occupiedStations.filter(s => s.type === 'pc');
  const occupiedPs = occupiedStations.filter(s => s.type === 'playstation');
  
  const totalRevenue = occupiedStations.reduce((sum, station) => sum + station.currentAmount, 0);
  const pendingOrders = state.orders.filter(order => order.status === 'pending');

  const handleOpenKiosk = (station) => {
    setSelectedStation(station);
  };

  const handleCloseKiosk = () => {
    setSelectedStation(null);
  };

  // Ensure we select a valid room if the current selection is invalid
  useEffect(() => {
    if (!selectedRoom && state.rooms.length > 0) {
      setSelectedRoom(state.rooms[0].id);
    }
  }, [state.rooms, selectedRoom]);

  const currentRoom = state.rooms.find(room => room.id === selectedRoom);

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-600">Übersicht über alle Räume und aktuellen Umsatz</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Aktive PCs</p>
              <p className="text-2xl font-bold text-gray-900">
                {occupiedPcs.length} / {allStations.filter(s => s.type === 'pc').length}
              </p>
            </div>
            <SafeIcon icon={FiMonitor} className="text-3xl text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Aktive PS</p>
              <p className="text-2xl font-bold text-gray-900">
                {occupiedPs.length} / {allStations.filter(s => s.type === 'playstation').length}
              </p>
            </div>
            <SafeIcon icon={FaPlaystation} className="text-3xl text-blue-700" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Aktueller Umsatz</p>
              <p className="text-2xl font-bold text-green-600">
                {totalRevenue.toFixed(2)} {state.settings.currency}
              </p>
            </div>
            <SafeIcon icon={FiDollarSign} className="text-3xl text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Offene Bestellungen</p>
              <p className="text-2xl font-bold text-orange-600">
                {pendingOrders.length}
              </p>
            </div>
            <SafeIcon icon={FiPackage} className="text-3xl text-orange-500" />
          </div>
        </div>
      </div>

      {/* Room Selector */}
      <div className="mb-6">
        <div className="flex space-x-2 overflow-x-auto pb-2">
          {state.rooms.map(room => (
            <button
              key={room.id}
              onClick={() => setSelectedRoom(room.id)}
              className={`px-6 py-3 rounded-lg whitespace-nowrap transition-colors font-medium ${
                selectedRoom === room.id
                  ? 'bg-blue-500 text-white shadow-md'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {room.name}
              <span className="ml-2 text-sm bg-opacity-20 bg-white px-2 py-0.5 rounded-full">
                {room.stations.filter(s => s.status === 'occupied').length}/{room.stations.length}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Stations Grid */}
      {currentRoom ? (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">{currentRoom.name}</h2>
            <div className="flex space-x-4 text-sm text-gray-600">
              <div className="flex items-center space-x-1">
                <SafeIcon icon={FiMonitor} />
                <span>{currentRoom.stations.filter(s => s.type === 'pc').length} PCs</span>
              </div>
              <div className="flex items-center space-x-1">
                <SafeIcon icon={FaPlaystation} />
                <span>{currentRoom.stations.filter(s => s.type === 'playstation').length} PS</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {currentRoom.stations.map(station => (
              <StationCard 
                key={station.id} 
                station={station} 
                roomName={currentRoom.name}
                onOpenKiosk={handleOpenKiosk}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          Wählen Sie einen Raum aus oder erstellen Sie einen neuen Raum in den Einstellungen.
        </div>
      )}

      {/* Kiosk Modal */}
      {selectedStation && (
        <KioskModal 
          station={selectedStation} 
          onClose={handleCloseKiosk}
        />
      )}
    </div>
  );
}

export default Dashboard;